import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Link } from 'lucide-react';
import { useChallenges } from '../context/ChallengeContext';
import Lightbox from './Lightbox';

const ChallengeDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { challenges } = useChallenges();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  
  const challenge = challenges.find(c => c.id === id);
  
  if (!challenge) {
    return (
      <div className="text-center py-10">
        <p className="text-gray-400">Challenge not found.</p>
        <button
          onClick={() => navigate('/')}
          className="mt-4 text-blue-400 hover:text-blue-300 transition-colors"
        >
          Return to challenges
        </button>
      </div>
    );
  }
  
  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'Composition':
        return 'bg-blue-500';
      case 'Lighting':
        return 'bg-amber-500';
      case 'Technical':
        return 'bg-emerald-500';
      case 'Creative':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  const handleImageClick = (index: number) => {
    setSelectedImageIndex(index);
    setLightboxOpen(true);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
      >
        <ArrowLeft size={20} />
        <span>Back to challenges</span>
      </button>
      
      <div className="space-y-8">
        <div>
          <span className={`inline-block text-xs px-2 py-1 rounded ${getCategoryColor(challenge.category)} text-white font-medium mb-3`}>
            {challenge.category}
          </span>
          <h1 className="text-3xl font-medium text-white mb-4">{challenge.title}</h1>
          <p className="text-gray-300 text-lg leading-relaxed whitespace-pre-line">{challenge.description}</p>
        </div>
        
        {challenge.inspiration && challenge.inspiration.length > 0 && (
          <section>
            <h2 className="text-xl font-medium text-white mb-4">Inspiration</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {challenge.inspiration.map((image, index) => (
                <div 
                  key={index} 
                  className="space-y-2 cursor-pointer group"
                  onClick={() => handleImageClick(index)}
                >
                  <div className="relative overflow-hidden rounded-lg">
                    <img
                      src={image.url}
                      alt={image.description}
                      className="w-full h-64 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-sm font-medium">Click to enlarge</span>
                    </div>
                  </div>
                  <p className="text-sm text-gray-400 italic">{image.description}</p>
                </div>
              ))}
            </div>
          </section>
        )}
        
        <div className="space-y-6">
          <section>
            <h2 className="text-xl font-medium text-white mb-3">Technical Guidance</h2>
            <ul className="space-y-2">
              {challenge.technicalGuidance.map((guidance, index) => (
                <li key={index} className="text-gray-300 flex items-start">
                  <span className="text-blue-400 mr-2">•</span>
                  {guidance}
                </li>
              ))}
            </ul>
          </section>
          
          <section>
            <h2 className="text-xl font-medium text-white mb-3">Creative Approach</h2>
            <ul className="space-y-2">
              {challenge.creativeApproach.map((approach, index) => (
                <li key={index} className="text-gray-300 flex items-start">
                  <span className="text-purple-400 mr-2">•</span>
                  {approach}
                </li>
              ))}
            </ul>
          </section>

          {challenge.featuredIn && challenge.featuredIn.length > 0 && (
            <section>
              <h2 className="text-xl font-medium text-white mb-3">Featured In</h2>
              <ul className="space-y-2">
                {challenge.featuredIn.map((feature, index) => (
                  <li key={index} className="text-gray-300 flex items-center gap-2">
                    <Link size={16} className="text-blue-400" />
                    <a 
                      href={feature.source}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-blue-400 transition-colors"
                    >
                      {feature.title}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>

      {challenge.inspiration && challenge.inspiration.length > 0 && (
        <Lightbox
          images={challenge.inspiration}
          initialIndex={selectedImageIndex}
          open={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
};

export default ChallengeDetail;